import type { Trigger } from "./types.ts";

export type FieldDef = {
  path: string;
  type: "string" | "number" | "boolean" | "datetime" | "enum";
  enumValues?: readonly string[];
  group: "call" | "company" | "rep" | "deal" | "email" | "meeting" | "event";
};

export const OUTCOME_VALUES = [
  "no_answer",
  "voicemail",
  "talked_callback",
  "demo_booked",
  "brochure_sent",
  "not_interested",
  "wrong_number",
  "do_not_call",
  "unlogged",
  "skipped",
] as const;
/** The 9 system lead statuses (the default list only; the editor lists the configured statuses for LEAD_STATUS_FIELD_PATHS). */
export const PROSPECT_STATUS_VALUES = [
  "new",
  "in_progress",
  "callback",
  "demo_booked",
  "in_pipeline", // GAVION call-brief-pipeline
  "not_interested",
  "bad_number",
  "do_not_call",
  "customer",
  "cooling_down",
] as const;
/** The default pipeline's values (the editor lists the configured stages for STAGE_FIELD_PATHS). */
export const DEAL_STAGE_VALUES = [
  "nouveau_lead",
  "qualifie",
  "proposition_envoyee",
  "negociation",
  "gagne",
  "perdu",
] as const;
/** Condition fields whose values are deal stages: the rule editor lists the configured stages (config.dealStages). */
export const STAGE_FIELD_PATHS: readonly string[] = [
  "deal.stage",
  "from_stage",
  "to_stage",
];
/** Condition fields whose values are lead statuses: the rule editor lists the configured statuses (config.leadStatuses). */
export const LEAD_STATUS_FIELD_PATHS: readonly string[] = [
  "company.prospect_status",
];
export const EMAIL_EVENT_TYPES = [
  "reply_received",
  "bounced",
  "complained",
  "opened",
  "clicked",
  "unsubscribed",
] as const;

const company: FieldDef[] = [
  { path: "company.name", type: "string", group: "company" },
  { path: "company.trade", type: "string", group: "company" },
  { path: "company.region", type: "string", group: "company" },
  { path: "company.email", type: "string", group: "company" },
  { path: "company.attempt_count", type: "number", group: "company" },
  { path: "company.fit_score", type: "number", group: "company" },
  { path: "company.high_quality", type: "boolean", group: "company" },
  {
    path: "company.prospect_status",
    type: "enum",
    enumValues: PROSPECT_STATUS_VALUES,
    group: "company",
  },
  { path: "company.last_called_at", type: "datetime", group: "company" },
  {
    path: "company.language",
    type: "enum",
    enumValues: ["fr", "en"],
    group: "company",
  },
];
const rep: FieldDef[] = [
  { path: "rep.id", type: "number", group: "rep" },
  { path: "rep.first_name", type: "string", group: "rep" },
  { path: "rep.microsoft_connected", type: "boolean", group: "rep" },
];
const deal: FieldDef[] = [
  { path: "deal.id", type: "number", group: "deal" },
  {
    path: "deal.stage",
    type: "enum",
    enumValues: DEAL_STAGE_VALUES,
    group: "deal",
  },
  { path: "deal.amount", type: "number", group: "deal" },
  { path: "deal.demo_at", type: "datetime", group: "deal" },
];

export const FIELD_CATALOG: Record<Trigger, readonly FieldDef[]> = {
  call_outcome: [
    {
      path: "call.outcome",
      type: "enum",
      enumValues: OUTCOME_VALUES,
      group: "call",
    },
    { path: "call.note", type: "string", group: "call" },
    { path: "call.callback_at", type: "datetime", group: "call" },
    { path: "call.demo_at", type: "datetime", group: "call" },
    { path: "call.demo_amount", type: "number", group: "call" },
    { path: "call.email", type: "string", group: "call" },
    { path: "call.not_interested_reason", type: "string", group: "call" },
    {
      path: "previous_outcome",
      type: "enum",
      enumValues: OUTCOME_VALUES,
      group: "event",
    },
    ...company,
    ...rep,
  ],
  deal_created: [...deal, ...company, ...rep],
  deal_stage_changed: [
    {
      path: "from_stage",
      type: "enum",
      enumValues: DEAL_STAGE_VALUES,
      group: "event",
    },
    {
      path: "to_stage",
      type: "enum",
      enumValues: DEAL_STAGE_VALUES,
      group: "event",
    },
    ...deal,
    ...company,
    ...rep,
  ],
  email_event: [
    {
      path: "type",
      type: "enum",
      enumValues: EMAIL_EVENT_TYPES,
      group: "event",
    },
    { path: "message.subject", type: "string", group: "email" },
    ...company,
    ...rep,
  ],
  meeting_note_matched: [
    { path: "note.title", type: "string", group: "meeting" },
    { path: "deal", type: "string", group: "deal" },
    ...deal,
    ...company,
  ],
  no_activity: [
    { path: "days_inactive", type: "number", group: "event" },
    ...deal,
    ...company,
    ...rep,
  ],
  schedule: [{ path: "now", type: "datetime", group: "event" }],
};

export const isKnownField = (trigger: Trigger, path: string): boolean =>
  FIELD_CATALOG[trigger].some((f) => f.path === path) ||
  path.startsWith("config.");
